import { createSignal, createMemo, Show, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { Skeleton } from "@/components/skeleton";
import { PanelKpi, PanelKpiGrid, RefreshIndicator } from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import { registerPanel } from "@/panel-registry";

interface IisSite {
  Name?: string;
  State?: string;
  ID?: number;
  ApplicationPool?: string;
  PhysicalPath?: string;
}

interface IisPool {
  Name?: string;
  State?: string;
  ManagedRuntimeVersion?: string;
  IdentityType?: string;
  AutoStart?: boolean;
}

interface IisBinding {
  protocol?: string;
  bindingInformation?: string;
  sslFlags?: number;
}

interface ListResult<T> {
  rows: T[];
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (Array.isArray(value)) return value.map((v) => formatCell(v)).join("; ");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const IisPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [selectedSite, setSelectedSite] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const sitesQuery = createToolQuery<ListResult<IisSite>>(() => ({
    tool: "iis_sites_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const poolsQuery = createToolQuery<ListResult<IisPool>>(() => ({
    tool: "iis_apppools_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const bindingsQuery = createToolQuery<ListResult<IisBinding>>(() => ({
    tool: "iis_bindings_list",
    params: { siteName: selectedSite() },
    enabled: enabled() && selectedSite().length > 0,
    refetchInterval: 0,
  }));

  const sitesRefresh = usePanelRefresh({
    query: () => ({
      isFetching: sitesQuery.isFetching,
      refetch: () => sitesQuery.refetch(),
      dataUpdatedAt: sitesQuery.dataUpdatedAt,
      error: sitesQuery.error,
    }),
  });
  const poolsRefresh = usePanelRefresh({
    query: () => ({
      isFetching: poolsQuery.isFetching,
      refetch: () => poolsQuery.refetch(),
      dataUpdatedAt: poolsQuery.dataUpdatedAt,
      error: poolsQuery.error,
    }),
  });
  const bindingsRefresh = usePanelRefresh({
    query: () => ({
      isFetching: bindingsQuery.isFetching,
      refetch: () => bindingsQuery.refetch(),
      dataUpdatedAt: bindingsQuery.dataUpdatedAt,
      error: bindingsQuery.error,
    }),
  });

  async function runSiteAction(
    siteName: string,
    actionId: "start" | "stop" | "restart",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!siteName) return;
    const ok = await confirm.confirm({
      title: `${label} - ${siteName}`,
      body: `Confirmer l'action IIS "${label}" sur le site ${siteName} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? siteName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${siteName}...`, level: "info" });
    try {
      await tools.call(
        "iis_site_action",
        { siteName, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} sur ${siteName} reussie.`, level: "info" });
      await sitesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function runPoolAction(
    poolName: string,
    actionId: "start" | "stop" | "recycle",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!poolName) return;
    const ok = await confirm.confirm({
      title: `${label} - ${poolName}`,
      body: `Confirmer l'action IIS "${label}" sur le pool ${poolName} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? poolName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${poolName}...`, level: "info" });
    try {
      await tools.call(
        "iis_apppool_action",
        { poolName, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} sur ${poolName} reussie.`, level: "info" });
      await poolsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  // ─── KPI cards — calculés depuis les données déjà chargées ─────────────
  const kpis = createMemo(() => {
    const sites = sitesQuery.data?.rows ?? [];
    const pools = poolsQuery.data?.rows ?? [];
    const sitesStarted = sites.filter((s) => String(s.State ?? "").toLowerCase() === "started").length;
    const poolsStarted = pools.filter((p) => String(p.State ?? "").toLowerCase() === "started").length;
    const poolsStopped = pools.filter((p) => String(p.State ?? "").toLowerCase() === "stopped").length;
    return {
      sites: sites.length,
      sitesStarted,
      pools: pools.length,
      poolsStarted,
      poolsStopped,
    };
  });

  const loading = () => sitesQuery.isFetching && !sitesQuery.data;

  const kpiCards = () => (
    <Show
      when={!loading()}
      fallback={
        <PanelKpiGrid>
          <For each={[0, 1, 2, 3]}>
            {() => (
              <div class="panel-kpi">
                <div class="panel-kpi__body">
                  <Skeleton variant="line" width="60%" height="11px" />
                  <Skeleton variant="line" width="40%" height="22px" />
                </div>
              </div>
            )}
          </For>
        </PanelKpiGrid>
      }
    >
      <PanelKpiGrid>
        <PanelKpi label="Sites" value={kpis().sites} sub={`${kpis().sitesStarted} demarres`} />
        <PanelKpi label="Pools actifs" value={`${kpis().poolsStarted}/${kpis().pools}`} intent="ok" />
        <PanelKpi label="Pools arretes" value={kpis().poolsStopped} intent="muted" />
        <PanelKpi label="App pools" value={kpis().pools} intent="info" />
      </PanelKpiGrid>
    </Show>
  );

  const siteColumns: Column<IisSite>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "ID", header: "ID" },
    { accessorKey: "ApplicationPool", header: "ApplicationPool" },
    { accessorKey: "PhysicalPath", header: "PhysicalPath" },
  ];

  const poolColumns: Column<IisPool>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "ManagedRuntimeVersion", header: "ManagedRuntimeVersion" },
    { accessorKey: "IdentityType", header: "IdentityType" },
    {
      accessorKey: "AutoStart",
      header: "AutoStart",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const bindingColumns: Column<IisBinding>[] = [
    { accessorKey: "protocol", header: "Protocol" },
    { accessorKey: "bindingInformation", header: "BindingInformation" },
    { accessorKey: "sslFlags", header: "SslFlags" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "sites",
      label: "Sites",
      render: () => (
        <>
          {kpiCards()}
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => sitesQuery.refetch()}>
              Rafraichir
            </button>
            <RefreshIndicator state={sitesRefresh} />
          </div>
          <Show when={!loading()} fallback={<Skeleton variant="table" rows={8} />}>
            <DataTable<IisSite>
              rows={sitesQuery.data?.rows ?? []}
              columns={siteColumns}
              rowActions={(r) => {
                const stateLower = String(r.State ?? "").toLowerCase();
                const isStarted = stateLower === "started";
                const isStopped = stateLower === "stopped";
                return (
                  <>
                    <button
                      type="button"
                      class="btn btn--xs btn--ghost"
                      disabled={isStarted}
                      onClick={() => runSiteAction(r.Name ?? "", "start", "Start", "info")}
                    >
                      Start
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--warn"
                      disabled={!isStarted}
                      onClick={() => runSiteAction(r.Name ?? "", "restart", "Restart", "warn")}
                    >
                      Restart
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--danger"
                      disabled={isStopped}
                      onClick={() => runSiteAction(r.Name ?? "", "stop", "Stop", "danger")}
                    >
                      Stop
                    </button>
                  </>
                );
              }}
            />
          </Show>
        </>
      ),
    },
    {
      id: "pools",
      label: "App Pools",
      render: () => (
        <>
          {kpiCards()}
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => poolsQuery.refetch()}>
              Rafraichir
            </button>
            <RefreshIndicator state={poolsRefresh} />
          </div>
          <Show when={!loading()} fallback={<Skeleton variant="table" rows={8} />}>
            <DataTable<IisPool>
              rows={poolsQuery.data?.rows ?? []}
              columns={poolColumns}
              rowActions={(r) => {
                const stateLower = String(r.State ?? "").toLowerCase();
                const isStarted = stateLower === "started";
                const isStopped = stateLower === "stopped";
                return (
                  <>
                    <button
                      type="button"
                      class="btn btn--xs btn--ghost"
                      disabled={isStarted}
                      onClick={() => runPoolAction(r.Name ?? "", "start", "Start", "info")}
                    >
                      Start
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--warn"
                      disabled={!isStarted}
                      onClick={() => runPoolAction(r.Name ?? "", "recycle", "Recycle", "warn")}
                    >
                      Recycle
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--danger"
                      disabled={isStopped}
                      onClick={() => runPoolAction(r.Name ?? "", "stop", "Stop", "danger")}
                    >
                      Stop
                    </button>
                  </>
                );
              }}
            />
          </Show>
        </>
      ),
    },
    {
      id: "bindings",
      label: "Bindings",
      render: () => (
        <>
          {kpiCards()}
          <div class="panel-toolbar panel-toolbar--filter">
            <select
              class="panel-filter-input"
              value={selectedSite()}
              onChange={(e) => setSelectedSite(e.currentTarget.value)}
            >
              <option value="">-- choisir un site --</option>
              <For each={sitesQuery.data?.rows ?? []}>
                {(s) => <option value={s.Name ?? ""}>{s.Name ?? ""}</option>}
              </For>
            </select>
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedSite()}
              onClick={() => bindingsQuery.refetch()}
            >
              Rafraichir
            </button>
            <RefreshIndicator state={bindingsRefresh} />
          </div>
          <DataTable<IisBinding>
            rows={bindingsQuery.data?.rows ?? []}
            columns={bindingColumns}
            emptyMessage={selectedSite() ? "Aucun binding." : "Selectionnez un site puis cliquez sur Rafraichir."}
          />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (sitesQuery.isFetching || poolsQuery.isFetching || bindingsQuery.isFetching)
      return { text: "Chargement...", level: "info" };
    const err = sitesQuery.error || poolsQuery.error || bindingsQuery.error;
    if (err)
      return {
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      };
    return undefined;
  };

  return <PanelLayout title="IIS" subtabs={subtabs} status={status()} />;
};

registerPanel("iis", IisPanel);
export default IisPanel;
